import { useEffect, useRef, useState } from 'preact/hooks';
import type { FrameOutput } from '@meteoradar/shared/contract/frame.schema';
import { GIF_FRAME_MS, GIF_LAST_FRAME_MS, GIF_MAX_SIZE_PX, GIF_OPTIONS_HOURS } from '../config.js';
import { formatFrameTime, formatWindow } from '../format.js';
import { type PlayerController } from '../player/controller.js';
import { composedFileName, composeFrame, deliverFile } from '../render/compose-frame.js';
import { exportGif } from '../render/export-gif.js';
import { currentFrame, currentProduct, exportProgress, latestFrame, playing, speed } from '../state/store.js';

type Props = {
  player: PlayerController;
  onError: (message: string) => void;
};

export const DownloadMenu = ({ player, onError }: Props) => {
  const [ open, setOpen ] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const frame = currentFrame.value;
  const latest = latestFrame.value;
  const imageOptions: { label: string; frame: FrameOutput }[] = [];
  if (!playing.value && frame !== null) {
    imageOptions.push({ label: `Imagen actual · ${formatFrameTime(frame.ts)}`, frame });
  }
  if (latest !== null && (playing.value || frame?.ts !== latest.ts)) {
    imageOptions.push({ label: `Última imagen · ${formatFrameTime(latest.ts)}`, frame: latest });
  }
  const progress = exportProgress.value;
  const busy = progress !== null;

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onPointerDown = (event: PointerEvent): void => {
      if (menuRef.current !== null && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [ open ]);

  const productLabel = (): string => currentProduct.value?.name ?? '';
  const productCode = (): string => currentProduct.value?.code ?? 'radar';

  const downloadPng = async (target: FrameOutput): Promise<void> => {
    setOpen(false);
    try {
      const bitmaps = await player.loadLayers(target);
      const blob = await composeFrame(bitmaps, target, productLabel());
      await deliverFile(blob, composedFileName(productCode(), target.ts, 'png'));
    } catch {
      onError('No se pudo generar la imagen');
    }
  };

  const downloadGif = async (hours: number): Promise<void> => {
    setOpen(false);
    exportProgress.value = { done: 0, total: 0 };
    try {
      const frames: FrameOutput[] = await player.fetchWindow(hours);
      if (frames.length === 0) {
        onError('No hay imágenes en ese rango');
        return;
      }
      await player.prefetch(frames, (done, total) => {
        exportProgress.value = { done, total };
      });
      const blob = await exportGif({
        frames,
        loadLayers: async item => await player.loadLayers(item),
        productLabel: productLabel(),
        delayMs: GIF_FRAME_MS / speed.value,
        lastDelayMs: GIF_LAST_FRAME_MS,
        maxSize: GIF_MAX_SIZE_PX,
        onProgress: (done, total) => {
          exportProgress.value = { done, total };
        },
      });
      const last = frames.at(-1);
      await deliverFile(blob, composedFileName(productCode(), last?.ts ?? new Date().toISOString(), 'gif'));
    } catch {
      onError('No se pudo generar el GIF');
    } finally {
      exportProgress.value = null;
    }
  };

  return (
    <div class="download" ref={menuRef}>
      <button
        type="button"
        class="viewer__action"
        onClick={() => setOpen(value => !value)}
        aria-label="Descargar"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={frame === null || busy}
      >
        {busy ? `${progress.done}/${progress.total || '…'}` : '⬇'}
      </button>
      {open && (
        <div class="download__menu" role="menu">
          {imageOptions.map(option => (
            <button
              key={option.label}
              type="button"
              class="download__item"
              role="menuitem"
              onClick={() => void downloadPng(option.frame)}
            >
              {option.label}
            </button>
          ))}
          <div class="download__group">GIF animado</div>
          {GIF_OPTIONS_HOURS.map(hours => (
            <button key={hours} type="button" class="download__item" role="menuitem" onClick={() => void downloadGif(hours)}>
              Últimas {formatWindow(hours)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
